import React from 'react';
import { err, ok } from 'neverthrow';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { z } from 'zod';

import {
    NonEmptyString128Schema,
    NonEmptyString255Schema,
    NonNegativeDecimalStringSchema,
    DecimalStringSchema,
    EmailSchema,
    FloatSchema,
    IntegerFromStringSchema,
    IntegerSchema,
    NonEmptyStringSchema,
    NonNegativeIntegerSchema,
    PositiveIntegerSchema,
    UuidSchema,
    PhoneSchema,
    ZipCodeSchema,
    NonNegativeFloatSchema,
    NonEmptyString64Schema,
    NonEmptyString32Schema,
    VATNumberSchema,
    HttpUrlSchema,
} from '../../src/shared/types';
import { ImageUploader } from '../../elements/form/ImageUploader';

import { CreateTypedFormSchema } from './typedForm';

import type { NonEmptyString64, NonEmptyString32, VATNumber,
    HttpUrl } from '../../src/shared/types';
import type { ChangeEvent } from 'react';
import type {
    NonNegativeDecimalString,
    Image,
    NonNegativeFloat,
    NonEmptyString255,
    NonEmptyString128,
    Email,
    Integer,
    NonEmptyString, Float, PositiveInteger, Uuid, NonNegativeInteger, Phone,
    ZipCode } from 'src/shared/types';
import type { TypedFormInputProps, ExtendedFormInputSchema } from './typedForm';
import type { SelectChangeEvent } from '@mui/material/Select/SelectInput';

const tfTransformations = {
    numberToString: (value: number | null) => value !== null && value !== undefined ? `${value}` : '',
    stringToDecimalString: (value: string, previousValue: string | null) => {
        if (value === '') {
            return '0';
        }

        const result = DecimalStringSchema.safeParse(value);
        return result.success ? `${parseFloat(value)}` : previousValue;
    },
    stringToInt: (value: string, previousValue: number | null) => {
        if (value === '') {
            return null;
        }

        const result = IntegerFromStringSchema.safeParse(value);
        return result.success ? result.data : previousValue;
    },
    stringToNumber: (value: string, previousValue: number | null) => {
        if (value === '') {
            return null;
        }

        const result = DecimalStringSchema.safeParse(value);
        return result.success ? parseFloat(value) : previousValue;
    },
    stringToNumberString: (value: string, previousValue: string | null) => {
        if (value === '') {
            return null;
        }

        const result = DecimalStringSchema.safeParse(value);
        return result.success ? value : previousValue;
    },
    stringToString: (value: string | null) => value ?? '',
    stringToString2: (value: string) => value ? NonEmptyStringSchema.parse(value) : null,
};

export const typedFormSchemas = {
    boolean: CreateTypedFormSchema<boolean, boolean, 'nonEmpty'>({
        innerDefaultValue: false,
        rule: (value) => ok(value),
    }),
    booleanNumber: CreateTypedFormSchema<number, NonNegativeInteger, 'nonEmpty'>({
        innerDefaultValue: NonNegativeIntegerSchema.parse(0),
        rule: (value) => ok(NonNegativeIntegerSchema.parse(value)),
    }),
    createTypedSelect: <T extends string>(): ExtendedFormInputSchema<T, T, 'nonEmpty'> => CreateTypedFormSchema<T, T, 'nonEmpty'>({
        innerDefaultValue: null as unknown as T,
        rule: (value) => ok(value),
    }),
    email: CreateTypedFormSchema<string | null, Email, 'nonEmpty' | 'email'>({
        innerDefaultValue: null as string | null,
        rule: (value) => {
            if (value === null) {
                return err('nonEmpty');
            }

            const result = EmailSchema.safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('email');
        },
    }),
    emailOptional: CreateTypedFormSchema<string | null, Email | null, 'email'>({
        innerDefaultValue: null as string | null,
        rule: (value) => {
            const result = EmailSchema.nullable().safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('email');
        },
    }),
    float: CreateTypedFormSchema<number | null, Float, 'float'>({
        innerDefaultValue: null as number | null,
        rule: (value) => {
            const result = FloatSchema.safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('float');
        },
    }),
    floatOptional: CreateTypedFormSchema<number | null, Float | null, 'float'>({
        innerDefaultValue: null as number | null,
        rule: (value) => {
            const result = FloatSchema.nullable().safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('float');
        },
    }),
    httpUrl: CreateTypedFormSchema<string | null, HttpUrl, 'nonEmpty' | 'httpUrl'>({
        innerDefaultValue: null as string | null,
        rule: (value) => {
            if (value === null) {
                return err('nonEmpty');
            }

            const result = HttpUrlSchema.safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('httpUrl');
        },
    }),
    httpUrlOptional: CreateTypedFormSchema<string | null, HttpUrl | null, 'httpUrl'>({
        innerDefaultValue: null as string | null,
        rule: (value) => {
            const result = HttpUrlSchema.nullable().safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('httpUrl');
        },
    }),
    image: CreateTypedFormSchema<Image | null, Image | null, 'uuid'>({
        innerDefaultValue: null as Image | null,
        rule: (value) => {
            const result = z.custom<Image | null>((data) => z.object({
                binary: z.custom<Blob>((data) => data instanceof Blob).optional(),
                data: NonEmptyStringSchema.optional(),
                id: UuidSchema.optional(),
                name: NonEmptyStringSchema.optional(),
                url: NonEmptyStringSchema.optional(),
            }).nullable().safeParse(data)).safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('uuid');
        },
    }),
    integer: CreateTypedFormSchema<number | null, Integer, 'integer'>({
        innerDefaultValue: null as number | null,
        rule: (value) => {
            const result = IntegerSchema.safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('integer');
        },
    }),
    integerArray: CreateTypedFormSchema<number[], Integer[], 'integer'>({
        innerDefaultValue: [] as number[],
        rule: (value) => {
            const result = IntegerSchema.array().safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('integer');
        },
    }),
    integerOptional: CreateTypedFormSchema<number | null, Integer | null, 'integer'>({
        innerDefaultValue: null as number | null,
        rule: (value) => {
            const result = IntegerSchema.nullable().safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('integer');
        },
    }),
    literalType: <T extends string>(literalValue: T): ExtendedFormInputSchema<T, T, 'nonEmpty'> => CreateTypedFormSchema<T, T, 'nonEmpty'>({
        innerDefaultValue: literalValue,
        rule: (value) => value === literalValue ? ok(value) : err('nonEmpty'),
    }),
    nonEmptyString: CreateTypedFormSchema<string | null, NonEmptyString, 'nonEmpty' | 'text'>({
        innerDefaultValue: null as string | null,
        rule: (value) => {
            if (value === null) {
                return err('nonEmpty');
            }

            const result = NonEmptyStringSchema.safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('text');
        },
    }),
    nonEmptyString128: CreateTypedFormSchema<string | null, NonEmptyString128, 'nonEmpty' | 'text' | 'tooLong128'>({
        innerDefaultValue: null as string | null,
        rule: (value) => {
            if (value === null) {
                return err('nonEmpty');
            }

            const result = NonEmptyString128Schema.safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            if (value.length > 128) {
                return err('tooLong128');
            }
            return err('text');
        },
    }),
    nonEmptyString255: CreateTypedFormSchema<string | null, NonEmptyString255, 'nonEmpty' | 'text' | 'tooLong255'>({
        innerDefaultValue: null as string | null,
        rule: (value) => {
            if (value === null) {
                return err('nonEmpty');
            }

            const result = NonEmptyString255Schema.safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            if (value.length > 255) {
                return err('tooLong255');
            }
            return err('text');
        },
    }),
    nonEmptyString32: CreateTypedFormSchema<string | null, NonEmptyString32, 'nonEmpty' | 'text' | 'tooLong32'>({
        innerDefaultValue: null as string | null,
        rule: (value) => {
            if (value === null) {
                return err('nonEmpty');
            }

            const result = NonEmptyString32Schema.safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            if (value.length > 32) {
                return err('tooLong32');
            }
            return err('text');
        },
    }),
    nonEmptyString64: CreateTypedFormSchema<string | null, NonEmptyString64, 'nonEmpty' | 'text' | 'tooLong64'>({
        innerDefaultValue: null as string | null,
        rule: (value) => {
            if (value === null) {
                return err('nonEmpty');
            }

            const result = NonEmptyString64Schema.safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            if (value.length > 64) {
                return err('tooLong64');
            }
            return err('text');
        },
    }),
    nonEmptyStringArray: CreateTypedFormSchema<string[], NonEmptyString[], 'text'>({
        innerDefaultValue: [] as string[],
        rule: (value) => {
            const result = NonEmptyStringSchema.array().safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('text');
        },
    }),
    nonEmptyStringOptional: CreateTypedFormSchema<string | null, NonEmptyString | null, 'text'>({
        innerDefaultValue: null as string | null,
        rule: (value) => {
            const result = NonEmptyStringSchema.nullable().safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('text');
        },
    }),
    nonNegativeDecimalString: CreateTypedFormSchema<string, NonNegativeDecimalString, 'nonNegativeInteger'>({
        innerDefaultValue: '0',
        rule: (value) => {
            const result = NonNegativeDecimalStringSchema.safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('nonNegativeInteger');
        },
    }),
    nonNegativeFloat: CreateTypedFormSchema<number | null, NonNegativeFloat, 'nonNegativeFloat'>({
        innerDefaultValue: null as number | null,
        rule: (value) => {
            const result = NonNegativeFloatSchema.safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('nonNegativeFloat');
        },
    }),
    nonNegativeFloatOptional: CreateTypedFormSchema<number | null, NonNegativeFloat | null, 'nonNegativeFloat'>({
        innerDefaultValue: null as number | null,
        rule: (value) => {
            const result = NonNegativeFloatSchema.nullable().safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('nonNegativeFloat');
        },
    }),
    nonNegativeInteger: CreateTypedFormSchema<number | null, NonNegativeInteger, 'nonNegativeInteger'>({
        innerDefaultValue: null as number | null,
        rule: (value) => {
            const result = NonNegativeIntegerSchema.safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('nonNegativeInteger');
        },
    }),
    nonNegativeIntegerOptional: CreateTypedFormSchema<number | null, NonNegativeInteger | null, 'nonNegativeInteger'>({
        innerDefaultValue: null as number | null,
        rule: (value) => {
            const result = NonNegativeIntegerSchema.nullable().safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('nonNegativeInteger');
        },
    }),

    phone: CreateTypedFormSchema<string | null, Phone, 'nonEmpty' | 'phone'>({
        innerDefaultValue: null as string | null,
        rule: (value) => {
            if (value === null) {
                return err('nonEmpty');
            }

            const result = PhoneSchema.safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('phone');
        },
    }),

    positiveInteger: CreateTypedFormSchema<number | null, PositiveInteger, 'positiveInteger'>({
        innerDefaultValue: null as number | null,
        rule: (value) => {
            const result = PositiveIntegerSchema.safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('positiveInteger');
        },
    }),

    positiveIntegerOptional: CreateTypedFormSchema<number | null, PositiveInteger | null, 'positiveInteger'>({
        innerDefaultValue: null as number | null,
        rule: (value) => {
            const result = PositiveIntegerSchema.nullable().safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('positiveInteger');
        },
    }),

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    select: <T extends string>([ value, ...values]: Array<T>): ExtendedFormInputSchema<T, T, 'nonEmpty'> => CreateTypedFormSchema<T, T, 'nonEmpty'>({
        innerDefaultValue: value,
        rule: (value) => ok(value),
    }),

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    selectOptional: <T extends string>(values: Array<T | null>): ExtendedFormInputSchema<T | null, T | null, 'nonEmpty'> => CreateTypedFormSchema<T | null, T | null, 'nonEmpty'>({
        innerDefaultValue: null,
        rule: (value) => ok(value),
    }),

    uuid: CreateTypedFormSchema<Uuid, Uuid, 'uuid'>({
        innerDefaultValue: null as unknown as Uuid,
        rule: (value) => {
            const result = UuidSchema.safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('uuid');
        },
    }),

    uuidOptional: CreateTypedFormSchema<string | null, Uuid | null, 'uuid'>({
        innerDefaultValue: null as string | null,
        rule: (value) => {
            const result = UuidSchema.nullable().safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('uuid');
        },
    }),
    vatNumber: CreateTypedFormSchema<string | null, VATNumber, 'nonEmpty' | 'vatNumber'>({
        innerDefaultValue: null as string | null,
        rule: (value) => {
            if (value === null) {
                return err('nonEmpty');
            }

            const result = VATNumberSchema.safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('vatNumber');
        },
    }),
    zipCode: CreateTypedFormSchema<string | null, ZipCode, 'nonEmpty' | 'zipCode'>({
        innerDefaultValue: null as string | null,
        rule: (value) => {
            if (value === null) {
                return err('nonEmpty');
            }

            const result = ZipCodeSchema.safeParse(value);
            if (result.success) {
                return ok(result.data);
            }

            return err('zipCode');
        },
    }),
};

type BaseInputProps = {
    fullWidth?: boolean;
    disabled?: boolean;
    endAdornment?: string;
    description?: string;
    sx?: Record<string, unknown>;
};

type OnChangeEvent<TEvent = ChangeEvent<HTMLInputElement>> = {
    onChange?: (e: TEvent) => void;
};

export const muiTfInputs = {
    Checkbox: ((args: TypedFormInputProps<boolean> & BaseInputProps & OnChangeEvent & { label: string }) => (
        <FormControl
            error={args.fieldState.error?.message !== undefined}
            sx={args.sx}
        >
            <FormControlLabel
                control={(
                    <Checkbox
                        checked={args.field.value}
                        color="primary"
                        disabled={args.disabled === true}
                        name={args.field.name}
                        onChange={(e) => {
                            args.field.onChange(e.target.checked);
                            if (args.onChange) {
                                args.onChange(e);
                            }
                        }}
                    />
                )}
                label={args.label}
            />
            {args.fieldState.error?.message !== undefined && <FormHelperText error>{args.fieldState.error?.message}</FormHelperText>}
            {args.description && <FormHelperText>{args.description}</FormHelperText>}
        </FormControl>
    )),
    CheckboxNumber: ((args: TypedFormInputProps<number> & BaseInputProps & OnChangeEvent & { label: string }) => (
        <FormControl
            error={args.fieldState.error?.message !== undefined}
            sx={args.sx}
        >
            <FormControlLabel
                control={(
                    <Checkbox
                        checked={args.field.value === 1}
                        color="primary"
                        disabled={args.disabled === true}
                        name={args.field.name}
                        onChange={(e) => {
                            args.field.onChange(e.target.checked ? 1 : 0);
                            if (args.onChange) {
                                args.onChange(e);
                            }
                        }}
                    />
                )}
                label={args.label}
            />
            {args.fieldState.error?.message !== undefined && <FormHelperText error>{args.fieldState.error?.message}</FormHelperText>}
            {args.description && <FormHelperText>{args.description}</FormHelperText>}
        </FormControl>
    )),
    DecimalString: ((args: TypedFormInputProps<string> & BaseInputProps & OnChangeEvent<ChangeEvent<HTMLInputElement | HTMLTextAreaElement>> & { label: string; decimalPlaces?: number }) => {
        return (
            <FormControl error={args.fieldState.error !== undefined} fullWidth={!!args.fullWidth} sx={args.sx}>
                <TextField
                    disabled={args.disabled === true}
                    fullWidth={!!args.fullWidth}
                    InputProps={{
                        inputProps: {
                            inputMode: 'numeric',
                            pattern: `^[0-9]*[,.]?[0-9]{0,${args.decimalPlaces ?? 2}}$`,
                        },
                        ...(args.endAdornment ? { endAdornment: args.endAdornment } : {}),
                    }}
                    label={args.label}
                    name={args.field.name}
                    onChange={(e) => {
                        args.field.onChange(`${tfTransformations.stringToDecimalString(e.target.value, args.field.value)}`);
                        if (args.onChange) {
                            args.onChange(e);
                        }
                    }}
                    value={args.field.value}
                />
                {args.fieldState.error?.message !== undefined && <FormHelperText error>{args.fieldState.error?.message}</FormHelperText>}
                {args.description && <FormHelperText>{args.description}</FormHelperText>}
            </FormControl>
        );
    }),
    Image: (args: TypedFormInputProps<Image | null>) => (
        <ImageUploader
            image={args.field.value}
            setImage={(image) => args.field.onChange(image)}
        />
    ),
    Integer: ((args: TypedFormInputProps<number | null> & BaseInputProps & OnChangeEvent<ChangeEvent<HTMLInputElement | HTMLTextAreaElement>> & { label: string }) => {
        return (
            <FormControl error={args.fieldState.error !== undefined} fullWidth={!!args.fullWidth} sx={args.sx}>
                <TextField
                    disabled={args.disabled === true}
                    fullWidth={!!args.fullWidth}
                    InputProps={{
                        inputProps: {
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                        },
                        ...(args.endAdornment ? { endAdornment: args.endAdornment } : {}),
                    }}
                    label={args.label}
                    name={args.field.name}
                    onChange={(e) => {
                        args.field.onChange(tfTransformations.stringToNumber(e.target.value, args.field.value));
                        if (args.onChange) {
                            args.onChange(e);
                        }
                    }}
                    value={tfTransformations.numberToString(args.field.value)}
                />
                {args.fieldState.error?.message !== undefined && <FormHelperText error>{args.fieldState.error?.message}</FormHelperText>}
                {args.description && <FormHelperText>{args.description}</FormHelperText>}
            </FormControl>
        );
    }),
    IntegerString: ((args: TypedFormInputProps<string | null> & BaseInputProps & OnChangeEvent<ChangeEvent<HTMLInputElement | HTMLTextAreaElement>> & { label: string }) => {
        return (
            <FormControl error={args.fieldState.error !== undefined} fullWidth={!!args.fullWidth} sx={args.sx}>
                <TextField
                    disabled={args.disabled === true}
                    fullWidth={!!args.fullWidth}
                    InputProps={{
                        inputProps: {
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                        },
                        ...(args.endAdornment ? { endAdornment: args.endAdornment } : {}),
                    }}
                    label={args.label}
                    name={args.field.name}
                    onChange={(e) => {
                        args.field.onChange(tfTransformations.stringToNumberString(e.target.value, args.field.value));
                        if (args.onChange) {
                            args.onChange(e);
                        }
                    }}
                    value={tfTransformations.stringToString(args.field.value)}
                />
                {args.fieldState.error?.message !== undefined && <FormHelperText error>{args.fieldState.error?.message}</FormHelperText>}
                {args.description && <FormHelperText>{args.description}</FormHelperText>}
            </FormControl>
        );
    }),
    LongString: ((args: TypedFormInputProps<string | null> & BaseInputProps & OnChangeEvent<ChangeEvent<HTMLInputElement | HTMLTextAreaElement>> & { label: string; placeholder?: string; size?: 'small' | 'medium'; skipTrim?: true }) => (
        <FormControl error={args.fieldState.error !== undefined} fullWidth={!!args.fullWidth} sx={args.sx}>
            <TextField
                disabled={args.disabled === true}
                fullWidth={!!args.fullWidth}
                InputLabelProps={args.placeholder !== undefined ? { shrink: true } : {}}
                label={args.label}
                multiline
                name={args.field.name}
                onBlur={args.skipTrim ? undefined : (e) => args.field.onChange(tfTransformations.stringToString2(e.target.value.trim()))}
                onChange={(e) => {
                    args.field.onChange(tfTransformations.stringToString2(e.target.value));
                    if (args.onChange) {
                        args.onChange(e);
                    }
                }}
                placeholder={args.placeholder}
                rows={4}
                size={args.size ?? 'medium'}
                value={tfTransformations.stringToString(args.field.value)}
            />
            {args.fieldState.error?.message !== undefined && <FormHelperText error>{args.fieldState.error?.message}</FormHelperText>}
            {args.description && <FormHelperText>{args.description}</FormHelperText>}
        </FormControl>
    )),
    Number: ((args: TypedFormInputProps<number | null> & BaseInputProps & OnChangeEvent<ChangeEvent<HTMLInputElement | HTMLTextAreaElement>> & { label: string; decimalPlaces?: number }) => {
        return (
            <FormControl error={args.fieldState.error !== undefined} fullWidth={!!args.fullWidth} sx={args.sx}>
                <TextField
                    disabled={args.disabled === true}
                    fullWidth={!!args.fullWidth}
                    InputProps={{
                        inputProps: {
                            inputMode: 'numeric',
                            pattern: `^[0-9]*[,.]?[0-9]{0,${args.decimalPlaces ?? 2}}$`,
                        },
                        ...(args.endAdornment ? { endAdornment: args.endAdornment } : {}),
                    }}
                    label={args.label}
                    name={args.field.name}
                    onChange={(e) => {
                        args.field.onChange(tfTransformations.stringToNumber(e.target.value, args.field.value));
                        if (args.onChange) {
                            args.onChange(e);
                        }
                    }}
                    value={tfTransformations.numberToString(args.field.value)}
                />
                {args.fieldState.error?.message !== undefined && <FormHelperText error>{args.fieldState.error?.message}</FormHelperText>}
                {args.description && <FormHelperText>{args.description}</FormHelperText>}
            </FormControl>
        );
    }),
    Select: <T extends string>(args: TypedFormInputProps<T> & BaseInputProps & OnChangeEvent<SelectChangeEvent<T>> & { options: Record<T, string>; label: string }) => (
        <FormControl error={args.fieldState.error !== undefined} fullWidth={!!args.fullWidth} sx={args.sx}>
            <InputLabel>{args.label}</InputLabel>
            <Select
                disabled={args.disabled === true}
                label={args.label}
                name={args.field.name}
                onChange={
                    (e) => {
                        args.field.onChange(e.target.value as T);
                        if (args.onChange) {
                            args.onChange(e);
                        }
                    }
                }
                value={args.field.value}
            >
                {Object.entries(args.options).map(([value, label]) => (
                    <MenuItem key={value} value={value}>{`${label}`}</MenuItem>
                ))}
            </Select>
            {args.fieldState.error?.message !== undefined && <FormHelperText error>{args.fieldState.error?.message}</FormHelperText>}
            {args.description && <FormHelperText>{args.description}</FormHelperText>}
        </FormControl>
    ),
    SelectNullable: <T extends string>(args: TypedFormInputProps<T | null> & BaseInputProps & OnChangeEvent<SelectChangeEvent<T>> & { options: Record<T, string>; label: string }) => (
        <FormControl error={args.fieldState.error !== undefined} fullWidth={!!args.fullWidth} sx={args.sx}>
            <InputLabel>{args.label}</InputLabel>
            <Select
                disabled={args.disabled === true}
                label={args.label}
                name={args.field.name}
                onChange={
                    (e) => {
                        args.field.onChange(e.target.value as T);
                        if (args.onChange) {
                            // @ts-expect-error
                            args.onChange(e);
                        }
                    }
                }
                value={args.field.value}
            >
                {Object.entries(args.options).map(([value, label]) => (
                    <MenuItem key={value} value={value}>{`${label}`}</MenuItem>
                ))}
            </Select>
            {args.fieldState.error?.message !== undefined && <FormHelperText error>{args.fieldState.error?.message}</FormHelperText>}
            {args.description && <FormHelperText>{args.description}</FormHelperText>}
        </FormControl>
    ),
    SelectNumber: <T extends number>(args: TypedFormInputProps<T | null> & BaseInputProps & OnChangeEvent<SelectChangeEvent<T>> & { options: Record<T, NonEmptyString>; label: string }) => (
        <FormControl error={args.fieldState.error !== undefined} fullWidth={!!args.fullWidth} sx={args.sx}>
            <InputLabel>{args.label}</InputLabel>
            <Select
                disabled={args.disabled === true}
                label={args.label}
                name={args.field.name}
                onChange={
                    (e) => {
                        args.field.onChange(tfTransformations.stringToNumber(e.target.value, args.field.value) as T);
                        if (args.onChange) {
                            //@ts-expect-error String is not number in event...
                            args.onChange(e);
                        }
                    }
                }
                value={tfTransformations.numberToString(args.field.value)}
            >
                {Object.entries(args.options).map(([value, label]) => (
                    <MenuItem key={value} value={value}>{`${label}`}</MenuItem>
                ))}
            </Select>
            {args.fieldState.error?.message !== undefined && <FormHelperText error>{args.fieldState.error?.message}</FormHelperText>}
            {args.description && <FormHelperText>{args.description}</FormHelperText>}
        </FormControl>
    ),
    String: ((args: TypedFormInputProps<string | null> & BaseInputProps & OnChangeEvent<ChangeEvent<HTMLInputElement | HTMLTextAreaElement>> & { label: string; placeholder?: string; size?: 'small' | 'medium'; skipTrim?: true; type?: 'password' }) => (
        <FormControl error={args.fieldState.error !== undefined} fullWidth={!!args.fullWidth} sx={args.sx}>
            <TextField
                disabled={args.disabled === true}
                fullWidth={!!args.fullWidth}
                InputLabelProps={args.placeholder !== undefined ? { shrink: true } : {}}
                label={args.label}
                name={args.field.name}
                onBlur={args.skipTrim ? undefined : (e) => args.field.onChange(tfTransformations.stringToString2(e.target.value.trim()))}
                onChange={(e) => {
                    args.field.onChange(tfTransformations.stringToString2(e.target.value));
                    if (args.onChange) {
                        args.onChange(e);
                    }
                }}
                placeholder={args.placeholder}
                size={args.size ?? 'medium'}
                type={args.type}
                value={tfTransformations.stringToString(args.field.value)}
            />
            {args.fieldState.error?.message !== undefined && <FormHelperText error>{args.fieldState.error?.message}</FormHelperText>}
            {args.description && <FormHelperText>{args.description}</FormHelperText>}
        </FormControl>
    )),
    Switch: ((args: TypedFormInputProps<boolean> & BaseInputProps & OnChangeEvent & { label: string }) => (
        <FormControl
            error={args.fieldState.error?.message !== undefined}
            sx={args.sx}
        >
            <FormControlLabel
                control={(
                    <Switch
                        checked={args.field.value}
                        name={args.field.name}
                        onChange={(e) => {
                            args.field.onChange(e.target.checked);
                            if (args.onChange) {
                                args.onChange(e);
                            }
                        }}
                    />
                )}
                label={args.label}
            />
            {args.fieldState.error?.message !== undefined && <FormHelperText error>{args.fieldState.error?.message}</FormHelperText>}
            {args.description && <FormHelperText>{args.description}</FormHelperText>}
        </FormControl>
    )),
};
